import { Injectable, inject } from '@angular/core';
import {
    HordeAuthorityDocumentSchema,
    HordeGameIntentDocumentSchema,
    HordeIntentSchema,
    hordePlayerSecretViewDocumentId,
    type HordeGameIntentDocument,
    type HordeIntent,
    type PersistedHordeGame,
    type PlayerSecretViewState,
    type ValidatedHordeGameIntentDocument,
} from '@animus/type/domain';
import type {
    HordeAuthorityState,
    HordeAuthorityTransition,
    HordeCommitOutcome,
    HordeSessionLoadResult,
    HordeSessionPersistence,
    HordeSessionSummary,
    RejectHordeIntentInput,
} from '@animus/utility/web/persistence';
import { map, type Observable } from 'rxjs';
import { z } from 'zod';
import {
    commitAuthorityTransition,
    reconcileIncompleteCommits as reconcileCommitJournal,
    writeProjections,
} from './horde-commit-journal';
import {
    classifyPublicSession,
    createPlayerSecretViews,
    parsePendingIntents,
    toAuthorityState,
    toPlayerSecretView,
    toPublicSessionSummaries,
} from './horde-session-projections';
import { withoutRxdbMetadata } from './rxdb-document.util';
import { RxDatabaseService } from './rxdb.service';

/**
 * RxDB-backed {@link HordeSessionPersistence}. Constructed through Angular DI: the database is injected via
 * {@link RxDatabaseService}, whose synchronous {@link RxDatabaseService.database} is set by the eager app
 * initializer. Tests construct the service and call `initialize()` first.
 */
@Injectable({ providedIn: 'root' })
export class RxDBHordeSessionPersistence implements HordeSessionPersistence {
    /** The application RxDB instance, ready once the eager app initializer resolves. */
    private readonly db = inject(RxDatabaseService).database;

    /** Commits at host through the authoritative persistence boundary. */
    async commitAtHost(intent: HordeIntent, transition: HordeAuthorityTransition): Promise<HordeCommitOutcome> {
        HordeIntentSchema.parse(intent);

        // Commits are already serialized by the session's `enqueueOperation` and the coordinator's `hostCommits`
        // concatMap, so the extra `navigator.locks` wrapper here is redundant for single-tab play - and it was
        // the source of an intermittent hard freeze on the round-start clear-commit: a Web Locks holder awaiting
        // an IndexedDB operation on the main thread can starve the lock queue (and every later commit) while the
        // UI thread is busy, deadlocking the walkthrough's CONFIRM_CLEAR_SPAWN_ZONE batch with no error.
        return this.commitWithLease(intent, transition);
    }

    /** Removes the public session projection and best-effort clears that game's event/intent/secret-view docs. */
    async deleteSession(gameId: string): Promise<void> {
        const db = this.db;
        const sessionDoc = await db['hordeSessions'].findOne(gameId).exec();

        if (sessionDoc) {
            await sessionDoc.remove();
        }

        const [events, intents, secretViews] = await Promise.all([
            db['hordeGameEvents'].find({ selector: { gameId } }).exec(),
            db['hordeGameIntents'].find({ selector: { gameId } }).exec(),
            db['hordePlayerSecretViews'].find({ selector: { gameId } }).exec(),
        ]);

        await Promise.all([...events, ...intents, ...secretViews].map((doc) => doc.remove()));
    }

    /** Persists an intent in the host outbox with its initial pending status. */
    async enqueueIntent(intent: HordeIntent, outboxScopeId: string): Promise<HordeGameIntentDocument> {
        HordeIntentSchema.parse(intent);
        const db = this.db;
        const event = z.json().parse(intent);
        const document = HordeGameIntentDocumentSchema.parse({
            acceptedRevision: null,
            actorDeviceId: intent.metadata.actorDeviceId,
            actorUserId: intent.metadata.actorUserId,
            createdAt: intent.metadata.createdAt,
            event,
            eventType: intent.type,
            expectedRevision: intent.metadata.expectedRevision,
            gameId: intent.metadata.gameId,
            intentId: intent.metadata.eventId,
            outboxScopeId,
            rejectionReason: null,
            status: 'pending',
            updatedAt: intent.metadata.createdAt,
            visibility: typeof intent.metadata.visibility === 'string' ? 'public' : 'privatePlayer',
        });

        await db['hordeGameIntents'].incrementalUpsert(document);

        return document;
    }

    /** Initializes authority in durable storage. */
    async initializeAuthority(state: HordeAuthorityState): Promise<void> {
        const authority = HordeAuthorityDocumentSchema.parse({
            game: state.game,
            gameId: state.game.gameId,
            vault: state.vault,
        });
        const db = this.db;

        await reconcileCommitJournal(db, state.game.gameId);
        const existingDoc = await db['hordeAuthorities'].findOne(state.game.gameId).exec();
        const existing = toAuthorityState(existingDoc);

        if (existing) {
            if (
                existing.game.host.deviceId !== state.game.host.deviceId ||
                existing.game.revision !== state.game.revision
            ) {
                throw new Error(`Game ${state.game.gameId} already has a different host authority.`);
            }
        } else {
            await db['hordeAuthorities'].insert(authority);
        }

        await writeProjections({ db, game: state.game, playerSecretViews: createPlayerSecretViews(state.vault) });
    }

    /** Lists public Horde projections for the canonical home screen. */
    async listPublicSessions(): Promise<HordeSessionSummary[]> {
        const db = this.db;

        return toPublicSessionSummaries(await db['hordeSessions'].find().exec());
    }

    /** Loads authority from durable or static storage. */
    async loadAuthority(gameId: string, hostDeviceId: string): Promise<HordeAuthorityState | null> {
        const db = this.db;

        await reconcileCommitJournal(db, gameId);
        const doc = await db['hordeAuthorities'].findOne(gameId).exec();
        const state = toAuthorityState(doc);

        return state?.game.host.deviceId === hostDeviceId ? state : null;
    }

    /** Loads player secret view from durable or static storage. */
    async loadPlayerSecretView(gameId: string, playerId: string): Promise<PlayerSecretViewState | null> {
        const db = this.db;
        const documentId = hordePlayerSecretViewDocumentId(gameId, playerId);
        const doc = await db['hordePlayerSecretViews'].findOne(documentId).exec();

        return toPlayerSecretView(doc);
    }

    /** Loads public session from durable or static storage. */
    async loadPublicSession(gameId: string): Promise<PersistedHordeGame | null> {
        const result = await this.loadPublicSessionResult(gameId);

        return result.status === 'ready' ? result.value : null;
    }

    /** Loads a public session without collapsing incompatible/corrupt records into not-found. */
    async loadPublicSessionResult(gameId: string): Promise<HordeSessionLoadResult> {
        try {
            const db = this.db;

            await reconcileCommitJournal(db, gameId);
            const doc = await db['hordeSessions'].findOne(gameId).exec();

            if (!doc) {
                return { status: 'notFound' };
            }

            return classifyPublicSession(doc.toMutableJSON());
        } catch (error) {
            return {
                message: error instanceof Error ? error.message : 'Saved-game storage is unavailable.',
                status: 'unavailable',
            };
        }
    }

    /** Observes authored intents as a reactive stream. */
    observeAuthoredIntents(gameId: string, actorDeviceId: string): Observable<HordeGameIntentDocument[]> {
        return this.db['hordeGameIntents']
            .find({
                selector: {
                    actorDeviceId,
                    gameId,
                },
            })
            .$.pipe(
                map((docs) =>
                    docs.flatMap((doc) => {
                        const parsed = HordeGameIntentDocumentSchema.safeParse(
                            withoutRxdbMetadata(doc.toMutableJSON())
                        );

                        return parsed.success ? [parsed.data] : [];
                    })
                )
            );
    }

    /** Observes pending intents as a reactive stream. */
    observePendingIntents(gameId: string): Observable<ValidatedHordeGameIntentDocument[]> {
        return this.db['hordeGameIntents']
            .find({
                selector: {
                    gameId,
                    status: 'pending',
                },
            })
            .$.pipe(map((docs) => parsePendingIntents(docs)));
    }

    /** Observes player secret view as a reactive stream. */
    observePlayerSecretView(gameId: string, playerId: string): Observable<PlayerSecretViewState | null> {
        const documentId = hordePlayerSecretViewDocumentId(gameId, playerId);

        return this.db['hordePlayerSecretViews'].findOne(documentId).$.pipe(map((doc) => toPlayerSecretView(doc)));
    }

    /** Observes public session as a reactive stream. */
    observePublicSession(gameId: string): Observable<PersistedHordeGame | null> {
        return this.db['hordeSessions'].findOne(gameId).$.pipe(
            map((doc): HordeSessionLoadResult =>
                doc ? classifyPublicSession(doc.toMutableJSON()) : { status: 'notFound' }
            ),
            map((result) => (result.status === 'ready' ? result.value : null))
        );
    }

    /** Streams local and replicated public-session index changes. */
    observePublicSessions(): Observable<HordeSessionSummary[]> {
        return this.db['hordeSessions'].find().$.pipe(map((docs) => toPublicSessionSummaries(docs)));
    }

    /** Marks an outbox intent rejected with its authoritative error details. */
    async rejectIntent({ intentId, reason, updatedAt }: RejectHordeIntentInput): Promise<void> {
        const db = this.db;
        const doc = await db['hordeGameIntents'].findOne(intentId).exec();

        if (!doc) {
            return;
        }

        await doc.incrementalPatch({
            rejectionReason: reason,
            status: 'rejected',
            updatedAt,
        });
    }

    /** Replays projections for any commit journal entry left behind by an interrupted commit. */
    async reconcileIncompleteCommits(gameId: string): Promise<void> {
        await reconcileCommitJournal(this.db, gameId);
    }

    /** Executes a host commit while the caller owns the per-game cross-tab authority lease. */
    private async commitWithLease(
        intent: HordeIntent,
        transition: HordeAuthorityTransition
    ): Promise<HordeCommitOutcome> {
        return commitAuthorityTransition({ db: this.db, intent, transition });
    }
}
